import { useEffect, useRef, useState } from "react";
import { inputCls, labelCls } from "components/tw/classes";

export interface SelectOption {
  value: string;
  label: string;
}

/** A labelled drop-down of a queue page's filter bar. */
export function FilterSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
}) {
  return (
    <label className="block w-full sm:w-48">
      <span className={labelCls}>{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)} className={inputCls}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * A search box that reports what was typed only after the person pauses for `delayMs`.
 * `value` is what the address holds; when it changes from outside (Back), the box follows.
 */
export function SearchBox({
  label,
  value,
  delayMs,
  onSearch,
}: {
  label: string;
  value: string;
  delayMs: number;
  onSearch: (text: string) => void;
}) {
  const [text, setText] = useState(value);
  const onSearchRef = useRef(onSearch);
  onSearchRef.current = onSearch;

  useEffect(() => setText(value), [value]);
  useEffect(() => {
    if (text === value) return;
    const timer = window.setTimeout(() => onSearchRef.current(text), delayMs);
    return () => window.clearTimeout(timer);
  }, [text, value, delayMs]);

  return (
    <label className="block w-full sm:w-64">
      <span className={labelCls}>{label}</span>
      <input
        type="search"
        value={text}
        onChange={(event) => setText(event.target.value)}
        autoComplete="off"
        className={inputCls}
      />
    </label>
  );
}

/** An on/off switch of the filter bar (a switch, not a checkbox: nothing in the lists is ticked). */
export function FilterToggle({
  label,
  isOn,
  onChange,
}: {
  label: string;
  isOn: boolean;
  onChange: (isOn: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={isOn}
      onClick={() => onChange(!isOn)}
      className="flex items-center gap-2 py-2 text-sm text-gray-700"
    >
      <span
        aria-hidden="true"
        className={`flex h-5 w-9 items-center rounded-full p-0.5 transition ${isOn ? "bg-indigo-600" : "bg-gray-300"}`}
      >
        <span className={`size-4 rounded-full bg-white transition ${isOn ? "translate-x-4" : ""}`} />
      </span>
      {label}
    </button>
  );
}
